import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { DateUtil, getLongDate } from 'src/app/common/util';
import { ButtonLoading } from 'src/app/models/button-loading.model';
import { EntryNote } from 'src/app/models/entry-note';
import { SimplePaginationModel } from 'src/app/models/pagination.model';
import { PurchaseOrderDetailResponse } from 'src/app/models/purchase-order.model';
import { ResponseObject } from 'src/app/models/response-object.model';
import { EntryNoteAssociationService } from 'src/app/services/entry-note-association.service';
import { EntryNoteService } from 'src/app/services/entry-note.service';
import { PurchaseOrderService } from 'src/app/services/purchase-order.service';
import { PurchasesAssociationService } from 'src/app/services/purchases-association.service';
import { PurchasesService } from 'src/app/services/purchases.service';

type OrderVM = {
  id: number;
  number: string;
  providerName: string;
  date: number | string;
};

type EntryNoteVM = {
  id: number;
  number: string;
  providerName: string;
  date: string;
};

@Component({
  selector: 'app-entrynote-order-association',
  templateUrl: './entrynote-order-association.component.html',
  styleUrls: ['./entrynote-order-association.component.scss']
})
export class EntrynoteOrderAssociationComponent implements OnInit {
  loading = false;

  allOrders: OrderVM[] = [];
  allEntryNotes: EntryNoteVM[] = [];

  orders: OrderVM[] = [];
  entryNotes: EntryNoteVM[] = [];

  filterOrders = '';
  filterEntryNotes = '';

  startDate = '';
  endDate = '';

  selectedEntryNoteId: number | null = null;
  selectedOrderId: number | null = null; // 👈 solo una orden por nota de entrada

  buttonAssociate: ButtonLoading = new ButtonLoading('btn-success', false, 'GUARDAR', 'fa-save');

  paginationOrders = new SimplePaginationModel(0, 5, 0, 0, 0);
  paginationEntryNotes = new SimplePaginationModel(0, 5, 0, 0, 0);

  currentOrderDetails: PurchaseOrderDetailResponse[] = [];
  currentEntryNote: EntryNote | null = null;
  currentRelatedId: number | null = null;

  // SVG
    @ViewChild('svgContainer', { static: true }) svgContainer: ElementRef;
    @HostListener('window:resize') onResize() { this.drawMultipleLinks(); }
  
    private timeoutOrders: any = null;
    private timeoutNotes: any = null;

  constructor(
    private purchasesAssociationService: PurchasesAssociationService,
    private entryNoteAssociationService: EntryNoteAssociationService,
    private entryNotesService: EntryNoteService,
    private purchaseOrderService: PurchaseOrderService,
    private toastService: ToastrService,
    private purchasesService: PurchasesService
  ) {}

  ngOnInit(): void {
    this.loadStartData();
  }

  /** Carga inicial */
  loadStartData() {
    this.loadEntryNotes();
    this.reloadOrders();
  }

  private normalizeEntryNote(en: EntryNote): EntryNoteVM {
    return {
      id: en.id,
      number: String(en.noteNumber),
      providerName: en.providerData?.name ?? '—',
      date: en.entryDate+"",
    };
  }

  private normalizeOrder(o: any): OrderVM {
    return {
      id: o.id,
      number: String(o.number),
      providerName: o.providerData?.name ?? '—',
      date: o.creationDate,
    };
  }

  /** Cargar notas de entrada */
  loadEntryNotes() {
    this.entryNotesService.getEntryNotesAll().subscribe({
      next: (resp: ResponseObject) => {
        this.entryNotes = (resp.data || []).map((en: any) => this.normalizeEntryNote(en));
        this.allEntryNotes = this.entryNotes;
        this.refreshEntryNotesPagination();
      },
      error: () => this.toastService.error('Error al obtener notas de entrada')
    });
  }

  /** Seleccionar una EntryNote */
  selectEntryNote(entryNoteId: number) {
    if (this.selectedEntryNoteId === entryNoteId) return;
    
    this.selectedEntryNoteId = entryNoteId;
    this.selectedOrderId = null;

    this.entryNotesService.getEntryNoteById(entryNoteId).subscribe({
      next: (resp: ResponseObject) => {
        this.currentEntryNote = resp.data;
        this.selectedOrderId = this.currentEntryNote?.purchaseOrderId || null;
        this.currentRelatedId = this.selectedOrderId;

        this.paginationOrders.page = 0;
        this.refreshOrdersPagination();
        this.getOrderDetails();
        this.drawMultipleLinks();
      },
      error: () => {
        this.currentEntryNote = null;
        this.toastService.error('Error al obtener detalles de la nota de entrada');
      }
    });
  }

  /** Seleccionar una orden (uno a uno) */
  toggleOrder(o: OrderVM) {
    
    if (!this.selectedEntryNoteId) return;
    
    if (this.isOrderLocked(o)) {
      this.toastService.warning('Esta compra ya está asociada a otra orden.');
      return;
    }

    const idx = this.selectedOrderId;
    if (idx != null) {
      if (idx === o.id) {
        this.selectedOrderId = null;
        this.currentRelatedId = null;
      } else {
        this.selectedOrderId = o.id;
        this.currentRelatedId = o.id;
      }
    } else {
      this.currentRelatedId = o.id;
      this.selectedOrderId = o.id;
    }
    this.getOrderDetails();
    this.refreshOrdersPagination();
    this.drawMultipleLinks();
  }

  /** Regla: una orden solo puede estar asociada a UNA entry note */
  isOrderLocked(o: OrderVM): boolean {
    
    // return o.associatedEntryNoteId != null && o.associatedEntryNoteId !== this.selectedEntryNoteId;
    // return this.currentEntryNote?.purchaseOrderId != null && this.currentEntryNote?.purchaseOrderId !== o.id;
    return false;
  }

  getDisabledSave(): boolean {
    return !this.selectedEntryNoteId || !this.selectedOrderId || this.buttonAssociate.loading;
  }

  /** Guardar la asociación */
  saveAssociation() {
    
    if (!this.selectedEntryNoteId || !this.selectedOrderId) return;

    if (this.selectedOrderId == this.currentEntryNote.purchaseOrderId) {
      this.toastService.info('Esta es la asociacion actual de la nota de credito', 'Info');
      return;
    } else {
      this.buttonAssociate.loading = true;
      this.entryNotesService.isExisteOtherAssociationEntrynoteOrder(this.selectedEntryNoteId, this.selectedOrderId).subscribe({
        next: (response: ResponseObject) => {
          if(!response.data) {
            this.save();
          } else {
            this.toastService.error("Esta Orden ya tiene una asociacion a otra nota de entrada", "Error")
            this.buttonAssociate.loading = false;
          }
        }, 
        error: (error: HttpErrorResponse) => {
          this.toastService.error("Error al verificar la existencia de la asociation", "Error")
          this.buttonAssociate.loading = false;
        }
      });
    }

    
  }

  save() {
    this.entryNoteAssociationService.associateEntryNoteToOrder(
      this.selectedEntryNoteId,
      this.selectedOrderId
    ).subscribe({
      next: () => {
        this.toastService.success('Asociación actualizada correctamente', 'Éxito');
        this.buttonAssociate.loading = false;
        this.selectEntryNote(this.selectedEntryNoteId!);
        this.reloadOrders();
        this.buttonAssociate.loading = false;
      },
      error: (e: HttpErrorResponse) => {
        this.toastService.error(e?.error?.message || 'Error al actualizar asociación', 'Error');
        this.buttonAssociate.loading = false;
      }
    });
  }

  /** Obtener detalles de la orden seleccionada */
  getOrderDetails() {
    if (!this.selectedOrderId) {
      this.currentOrderDetails = [];
      return;
    }

    this.purchaseOrderService.getOrder(this.selectedOrderId).subscribe({
      next: (resp: ResponseObject) => {
        this.currentOrderDetails = resp.data || [];
      },
      error: () => this.toastService.error('Error al obtener detalles de la orden')
    });
  }

  /** 🔄 Paginación de órdenes */
  refreshEntryNotesPagination() {
    const q = (this.filterEntryNotes || '').toLowerCase();
    const filtered = this.allEntryNotes.filter(en =>
      (en.number || '').toLowerCase().includes(q) ||
      (en.providerName || '').toLowerCase().includes(q)
    );

    this.paginationEntryNotes.total = filtered.length;
    this.paginationEntryNotes.start = this.paginationEntryNotes.page * this.paginationEntryNotes.size;
    const end = this.paginationEntryNotes.start + this.paginationEntryNotes.size;
    this.paginationEntryNotes.end = Math.min(end, filtered.length);

    this.entryNotes = filtered.slice(this.paginationEntryNotes.start, this.paginationEntryNotes.end);
    this.drawMultipleLinks();
  }

  /** 🔄 Recargar órdenes */
  reloadOrders() {
    const start = getLongDate(this.startDate);
    const end = getLongDate(this.endDate);

    this.purchaseOrderService.getOrdersActive().subscribe({
      next: (resp: ResponseObject) => {
        this.orders = (resp.data || []).map((p: any) => this.normalizeOrder(p));
        this.allOrders = this.orders;
        // this.resetAllPurchasesToBase();

        // if (this.selectedOrderId && this.currentRelatedPurchaseIds?.length) {
        //   const cached = this.associatedPurchasesCache.get(this.selectedOrderId) || [];
        //   if (cached.length) this.upsertAssociatedIntoAllPurchases(cached, this.selectedOrderId);

        //   const relatedSet = new Set(this.currentRelatedPurchaseIds);
        //   this.allPurchases = this.allPurchases.map(p => ({
        //     ...p,
        //     associatedOrderId: relatedSet.has(p.id) ? this.selectedOrderId : p.associatedOrderId ?? null
        //   }));
        // }

        this.paginationOrders.page = 0;
        this.refreshOrdersPagination();
        this.drawMultipleLinks();
      },
      error: () => this.toastService.error('Error al obtener compras')
    });
  }

  refreshOrdersPagination() {
    const q = (this.filterOrders || '').toLowerCase();
    const matches = (p: OrderVM) =>
      (p.number || '').toLowerCase().includes(q) ||
      (p.providerName || '').toLowerCase().includes(q);

    const associatedSet = new Set();
    associatedSet.add(this.currentEntryNote?.purchaseOrderId);
    
    const pinned = this.allOrders.filter(p => associatedSet.has(p.id));

    const restRaw = this.allOrders.filter(p => !associatedSet.has(p.id) && matches(p));

    const score = (x: OrderVM) => {
      if (x.id == this.selectedEntryNoteId) return 1;
      return 2;                               
    };
    const rest = restRaw.sort((a, b) => score(a) - score(b));

    const final = [...pinned, ...rest];

    this.paginationOrders.total = final.length;
    this.paginationOrders.start = this.paginationOrders.page * this.paginationOrders.size;
    const end = this.paginationOrders.start + this.paginationOrders.size;
    this.paginationOrders.end = Math.min(end, final.length);

    this.orders = final.slice(this.paginationOrders.start, this.paginationOrders.end);

    this.drawMultipleLinks();
  }

  debouncedFilterNotes() {
    clearTimeout(this.timeoutNotes);
    this.timeoutNotes = setTimeout(() => {
      this.paginationEntryNotes.page = 0;
      this.refreshEntryNotesPagination();
    }, 250);
  }

  debouncedFilterOrders() {
    clearTimeout(this.timeoutOrders);
    this.timeoutOrders = setTimeout(() => {
      this.paginationOrders.page = 0;
      this.refreshOrdersPagination();
    }, 250);
  }

  /** Dibuja líneas desde la orden seleccionada hacia cada compra actualmente “seleccionada” */
  drawMultipleLinks() {
    const svg = this.svgContainer?.nativeElement as SVGElement;
    if (!svg) return;

    svg.innerHTML = '';
    
    if (!this.selectedOrderId || !this.selectedEntryNoteId) return;
    setTimeout(() => {
      const parentRect = (svg.parentElement as HTMLElement).getBoundingClientRect();
      const selectedRow = document.querySelector('#table-notes tr.selected') as HTMLElement;
      if (!selectedRow) return;

      const selectedRect = selectedRow.getBoundingClientRect();
      const x1 = selectedRect.right - parentRect.left - 12;
      const y1 = selectedRect.top + selectedRect.height / 2 - parentRect.top;

      if (!this.currentRelatedId) return;
      const pid = this.selectedOrderId;
      
      const relatedRow = document.querySelector(`#table-orders tr[data-id="${pid}"]`) as HTMLElement;
      if (!relatedRow) return;

      const rect = relatedRow.getBoundingClientRect();
      const x2 = rect.left - parentRect.left - 12;
      const y2 = rect.top + rect.height / 2 - parentRect.top;
      const xMiddle = x1 + ((x2 - x1) / 2);

      this.drawLine(svg, x1, xMiddle, y1, y1);
      this.drawLine(svg, xMiddle, x2, y2, y2);
      this.drawLine(svg, xMiddle, xMiddle, y1, y2);

    }, 50);
  }

  private drawLine(svg: SVGElement, x1: number, x2: number, y1: number, y2: number) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', String(x1));
    line.setAttribute('y1', String(y1));
    line.setAttribute('x2', String(x2));
    line.setAttribute('y2', String(y2));
    line.setAttribute('stroke', '#001f54');
    line.setAttribute('stroke-width', '2');
    svg.appendChild(line);
  }
}
